import { ReactNode, useState } from "react";
import { CalendarDays, Flame, Share2, Users } from "lucide-react";
import { PackTable, usePack } from "./Pack";
import { Snapshot } from "@/game/types";
import { BoardSize, CONTROL_MODES, ControlMode, Platform, STICK_SIZES, StickSize, currentStreak, loadSkin, loadStreak, nextStreakReward, saveSkin, streakBonusStars, totalStars } from "@/game/storage";
import { SKINS, Skin, pupSprite } from "@/game/sprites";
import { Lock, Star } from "lucide-react";
import { DailyEntry, loadName, saveName, submitDailyScore, submitScore } from "@/game/api";
import { ShareResult, shareDaily, shareScore } from "@/game/share";
import { toast } from "sonner";
import { LANGS, Lang, getLang, setLang, t } from "@/game/i18n";
import { Leaderboard } from "./Leaderboard";
import { DailyLeaderboard } from "./DailyLeaderboard";
import { LevelGrid } from "./LevelGrid";
import { Dims, isBonusAfter } from "@/game/level";

const Modal = ({ children, testId, footer }: { children: ReactNode; testId: string; footer?: ReactNode }) => (
  <div data-testid={testId} className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-md animate-fade-in">
    <div className="modal-card animate-pop">
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  </div>
);

export const LangSwitch = () => {
  const cur = getLang();
  return (
    <div data-testid="lang-switch" className="flex items-center gap-1" role="group" aria-label={t("language")}>
      {LANGS.map((l) => (
        <button key={l.code} type="button" data-testid={`lang-${l.code}`} onClick={() => setLang(l.code as Lang)} className={`lang-btn ${cur === l.code ? "lang-btn-on" : ""}`} aria-pressed={cur === l.code}>{l.label}</button>
      ))}
    </div>
  );
};

const BOARDS: { v: BoardSize; k: "board11" | "board9" | "boardTall" }[] = [{ v: "11", k: "board11" }, { v: "9", k: "board9" }, { v: "tall", k: "boardTall" }];

export const SkinPicker = ({ skin, onChange }: { skin: Skin; onChange: (s: Skin) => void }) => {
  const stars = totalStars();
  return (
    <div data-testid="skin-picker" className="flex flex-col gap-1.5">
      <span className="hud-label">{t("skins")} · <Star size={11} className="inline text-yellow-300 fill-yellow-300" /> <span data-testid="total-stars">{stars}</span></span>
      <div className="flex gap-2 flex-wrap">
        {SKINS.map((sk) => {
          const unlocked = stars >= sk.stars;
          return (
            <button key={sk.id} type="button" data-testid={`skin-${sk.id}`} disabled={!unlocked} aria-pressed={skin === sk.id}
              onClick={() => { saveSkin(sk.id); onChange(sk.id); }}
              className={`skin-btn ${skin === sk.id ? "skin-btn-on" : ""} ${unlocked ? "" : "skin-btn-locked"}`}
              title={unlocked ? sk.id : t("unlockAt", { n: sk.stars })} style={{ borderColor: sk.color }}>
              <span className="skin-pup" style={{ backgroundImage: `url(${pupSprite(sk.id, "pup_down")})` }} />
              {!unlocked && <span className="skin-lock"><Lock size={12} /> {sk.stars}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const StreakBadge = () => {
  const count = currentStreak();
  const next = nextStreakReward(count);
  const bonus = streakBonusStars(loadStreak().best);
  return (
    <div data-testid="daily-streak" className="flex items-center gap-2 flex-wrap text-xs text-white/85">
      <Flame size={14} className={count > 0 ? "text-orange-400" : "text-white/40"} />
      <span className="hud-label">{t("streak")}</span>
      <span data-testid="daily-streak-days" className="font-bold text-orange-300">{count > 0 ? t("streakDays", { n: count }) : t("streakStart")}</span>
      {next && count > 0 && <span className="text-white/60">· {t("streakNext", { s: next.stars, n: next.days })}</span>}
      {bonus > 0 && <span data-testid="daily-streak-bonus" className="text-yellow-300">· {t("streakBonus", { s: bonus })}</span>}
    </div>
  );
};

export const StartScreen = ({ best, onPlay, onDaily, onHelp, onBoard, onLevels, onPack, onPup, packName, onInstall, boardSize, onBoardSize, skin, onSkin }: { best: number; onPlay: () => void; onDaily: () => void; onHelp: () => void; onBoard: () => void; onLevels: () => void; onPack: () => void; onPup: () => void; packName?: string | null; onInstall?: () => void; boardSize?: BoardSize; onBoardSize?: (b: BoardSize) => void; skin: Skin; onSkin: (s: Skin) => void }) => (
  <div data-testid="start-screen" className="relative w-full max-w-[520px] rounded-3xl overflow-hidden border-4 border-white/70 shadow-2xl animate-pop">
    <img src="/sprites/cave_bg.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2a0c04] via-[#2a0c04]/65 to-transparent" />
    <div className="absolute top-3 right-3 z-10"><LangSwitch /></div>
    <div className="relative px-6 pt-8 pb-8 flex flex-col items-start gap-4">
      <button
        type="button"
        data-testid="start-pup"
        aria-label="Rocco the pup"
        onClick={(e) => { onPup(); const el = e.currentTarget; el.classList.remove("pup-hop"); void el.offsetWidth; el.classList.add("pup-hop"); }}
        className="w-36 h-36 sm:w-44 sm:h-44 drop-shadow-[0_10px_20px_rgba(0,0,0,0.5)] animate-bob self-center bg-no-repeat cursor-pointer select-none"
        style={{ backgroundImage: `url(${pupSprite(skin, "pup_down")})`, backgroundSize: "400% 100%", backgroundPosition: "0 0" }}
      />
      <span className="eyebrow">{t("tagline")}</span>
      <h1 className="title-h1">{t("title")}</h1>
      <p className="text-sm sm:text-base text-white/90 leading-relaxed max-w-md">{t("story")}</p>
      <div className="flex items-center gap-3 flex-wrap mt-2">
        <button data-testid="start-game-button" onClick={onPlay} className="btn-primary">{t("play")}</button>
        <button data-testid="daily-challenge-button" onClick={onDaily} className="btn-daily" title={t("dailyHint")}><CalendarDays size={16} /> {t("daily")}</button>
        <button data-testid="level-select-button" onClick={onLevels} className="btn-ghost">{t("levels")}</button>
        <button data-testid="how-to-play-button" onClick={onHelp} className="btn-ghost">{t("howTo")}</button>
        <button data-testid="leaderboard-button" onClick={onBoard} className="btn-ghost">{t("top10")}</button>
        <button data-testid="pack-button" onClick={onPack} className="btn-ghost flex items-center gap-2"><Users size={16} /> {packName ?? t("pack")}</button>
        {onInstall && <button data-testid="install-app-button" onClick={onInstall} className="btn-ghost">{t("install")}</button>}
      </div>
      {boardSize && onBoardSize && (
        <div data-testid="board-size-switch" className="flex items-center gap-1 flex-wrap" role="group" aria-label={t("board")}>
          <span className="hud-label mr-1">{t("board")}</span>
          {BOARDS.map((b) => (
            <button key={b.v} type="button" data-testid={`board-${b.v}`} onClick={() => onBoardSize(b.v)} className={`lang-btn ${boardSize === b.v ? "lang-btn-on" : ""}`} aria-pressed={boardSize === b.v}>{t(b.k)}</button>
          ))}
        </div>
      )}
      <SkinPicker skin={skin} onChange={onSkin} />
      <StreakBadge />
      <div className="hud-label mt-2">{t("bestScore")} <span data-testid="start-best-score" className="text-yellow-300 font-mono">{best}</span></div>
      <div data-testid="start-footer" className="mt-3 text-[11px] text-white/55 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>© 2026 Hitnoje</span>
        <span>·</span>
        <a data-testid="privacy-link" href="/privacy.html" target="_blank" rel="noopener" className="underline underline-offset-2 hover:text-white">{t("privacy")}</a>
        <span>·</span>
        <a data-testid="contact-link" href="mailto:hitnojebgd@gmail.com" className="underline underline-offset-2 hover:text-white">hitnojebgd@gmail.com</a>
      </div>
    </div>
  </div>
);

export const LevelSelect = ({ unlocked, dims, onPick, onClose }: { unlocked: number; dims: Dims; onPick: (level: number) => void; onClose: () => void }) => (
  <Modal testId="level-select-modal">
    <h2 className="title-h2">{t("levelSelect")}</h2>
    <p className="text-sm text-white/70 mt-1">{t("unlockedUpTo")} <span data-testid="unlocked-level" className="text-yellow-300 font-bold">{unlocked}</span>. {t("scoreFromZero")}</p>
    <LevelGrid unlocked={unlocked} dims={dims} onPick={onPick} />
    <button data-testid="close-level-select-button" onClick={onClose} className="btn-ghost mt-3 w-full">{t("close")}</button>
  </Modal>
);

const HOWTO_KEYS = ["h1", "h2", "h3", "h3b", "h3c", "h4", "h5", "h6", "h7", "h8", "h9", "h10", "h11", "h12"] as const;

export const HowToPlay = ({ onClose }: { onClose: () => void }) => (
  <Modal testId="how-to-play-modal">
    <h2 className="title-h2">{t("howToTitle")}</h2>
    <ul className="mt-4 space-y-2 text-sm sm:text-base text-white/85 list-disc pl-5">
      {HOWTO_KEYS.map((k) => <li key={k}>{t(k)}</li>)}
    </ul>
    <button data-testid="close-instructions-button" onClick={onClose} className="btn-primary mt-6 w-full">{t("gotIt")}</button>
  </Modal>
);

export const InstallNudge = ({ onYes, onLater }: { onYes: () => void; onLater: () => void }) => (
  <Modal testId="install-nudge-modal">
    <h2 className="title-h2">{t("installNudgeTitle")}</h2>
    <p className="text-sm text-white/80 mt-2">{t("installNudgeText")}</p>
    <button data-testid="install-nudge-yes" onClick={onYes} className="btn-primary mt-6 w-full">{t("installYes")}</button>
    <button data-testid="install-nudge-later" onClick={onLater} className="btn-ghost mt-2 w-full">{t("installLater")}</button>
  </Modal>
);

export const InstallHelp = ({ onClose }: { onClose: () => void }) => (
  <Modal testId="install-help-modal">
    <h2 className="title-h2">{t("installTitle")}</h2>
    <p className="text-sm text-white/80 mt-2">{t("installLead")}</p>
    <ol className="mt-4 space-y-2 text-sm sm:text-base text-white/85 list-decimal pl-5">
      <li>{t("installStep1")} <span data-testid="install-url" className="font-mono text-yellow-300 break-all">{window.location.origin}</span></li>
      <li>{t("installStep2")}</li>
      <li>{t("installStep3")}</li>
      <li>{t("installStep4")}</li>
    </ol>
    <button data-testid="close-install-help-button" onClick={onClose} className="btn-primary mt-6 w-full">{t("gotIt")}</button>
  </Modal>
);

export const Ending = ({ snap, onRestart, onBoard }: { snap: Snapshot; onRestart: () => void; onBoard: () => void }) => (
  <div data-testid="ending-overlay" className="fixed inset-0 z-50 flex flex-col items-center justify-end ending-scene">
    <div className="ending-sky" />
    <div className="ending-sun" />
    <div className="ending-cave" />
    <div className="ending-ground" />
    <div className="ending-pup" role="img" aria-label="Rocco leaves the cave" style={{ backgroundImage: `url(${pupSprite(loadSkin() as Skin, "pup_side")})` }} />
    <div className="relative z-10 modal-card max-w-md text-center mb-6 mx-4 animate-pop" data-testid="ending-card">
      <span className="eyebrow text-yellow-300">{t("endingEyebrow")}</span>
      <h2 className="title-h2 mt-1">{t("endingTitle")}</h2>
      <p className="text-sm sm:text-base text-white/85 mt-2">{t("endingText")}</p>
      <div className="hud-label mt-4">{t("totalScore")}</div>
      <div data-testid="ending-score" className="stat-value text-3xl">{snap.score}</div>
      <div className="flex gap-3 justify-center mt-5 flex-wrap">
        <button data-testid="ending-restart-button" onClick={onRestart} className="btn-primary">{t("playAgain")}</button>
        <button data-testid="ending-leaderboard-button" onClick={onBoard} className="btn-ghost">{t("top10")}</button>
        <ShareButton score={snap.score} level={50} finished />
      </div>
    </div>
  </div>
);

export const LevelClear = ({ snap, onNext, countdown }: { snap: Snapshot; onNext: () => void; countdown: number }) => {
  const win = snap.bonus && snap.bonusResult === "win";
  const fail = snap.bonus && snap.bonusResult === "fail";
  const toTunnel = !snap.bonus && isBonusAfter(snap.level);
  return (
    <Modal testId="level-clear-overlay">
      <span className="eyebrow">{snap.bonus ? t("secretTunnel") : t("levelCleared", { n: snap.level })}</span>
      <h2 className="title-h2 mt-1" data-testid="level-clear-title">{win ? t("foundShortcut") : fail ? (snap.timedOut ? t("timeUp") : t("tunnelCollapsed")) : t("caveClean")}</h2>
      {snap.bonus && (
        <p data-testid="bonus-result-text" className="mt-2 text-sm text-white/80">
          {win ? t("skipLevel", { a: snap.level + 1, b: snap.level + 2 }) : t("noShortcut", { a: snap.level + 1 })}
        </p>
      )}
      <div className="mt-4 flex gap-6">
        <div><div className="hud-label">{t("bonus")}</div><div className="stat-value">{win ? "+2000" : fail ? "0" : "+1000"}</div></div>
        <div><div className="hud-label">{t("score")}</div><div data-testid="level-clear-score" className="stat-value">{snap.score}</div></div>
      </div>
      <p data-testid="level-clear-countdown" className="mt-4 text-sm text-white/70">{t("startsIn", { what: toTunnel ? t("secretTunnel") : t("nextLevel") })} <b className="text-yellow-300">{countdown}</b>…</p>
      <div className="mt-3 flex gap-2">
        <button data-testid="next-level-button" onClick={onNext} className="btn-primary flex-1 whitespace-nowrap">{toTunnel ? t("tunnelBtn") : t("nextLevelBtn")}</button>
        <ShareButton score={snap.score} level={snap.level} finished={false} compact />
      </div>
    </Modal>
  );
};

export const LeaderboardModal = ({ onClose, platform }: { onClose: () => void; platform: Platform }) => {
  const [tab, setTab] = useState<"classic" | "daily">("classic");
  return (
    <Modal testId="leaderboard-modal">
      <h2 className="title-h2 mb-3">{t("topPlayers")}</h2>
      <div className="flex gap-1 mb-4" role="group">
        <button type="button" data-testid="leaderboard-tab-classic" className={`lang-btn ${tab === "classic" ? "lang-btn-on" : ""}`} onClick={() => setTab("classic")}>{t("classic")}</button>
        <button type="button" data-testid="leaderboard-tab-daily" className={`lang-btn ${tab === "daily" ? "lang-btn-on" : ""}`} onClick={() => setTab("daily")}>{t("daily")}</button>
      </div>
      {tab === "classic" ? <Leaderboard /> : <DailyLeaderboard platform={platform} />}
      <button data-testid="close-leaderboard-button" onClick={onClose} className="btn-primary mt-6 w-full">{t("close")}</button>
    </Modal>
  );
};

const fmtSec = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

export const DailyOver = ({ snap, onRestart, onQuit, platform, packKey = 0 }: { snap: Snapshot; onRestart: () => void; onQuit: () => void; platform: Platform; packKey?: number }) => {
  const pack = usePack(packKey);
  const [name, setName] = useState(loadName);
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [saved, setSaved] = useState<DailyEntry | undefined>();
  const [refresh, setRefresh] = useState(0);
  const seconds = Math.floor(snap.elapsed / 1000);

  const submit = async () => {
    const n = name.trim().slice(0, 16);
    if (!n) return;
    setStatus("sending");
    try {
      saveName(n);
      const r = await submitDailyScore(n, snap.score, snap.wave, seconds, platform);
      setSaved(r);
      setStatus("done");
      setRefresh((k) => k + 1);
    } catch {
      setStatus("error");
    }
  };

  return (
    <Modal testId="daily-over-overlay" footer={
      <div className="flex flex-col gap-2">
        <button data-testid="daily-restart-button" onClick={onRestart} className="btn-primary w-full">{t("playAgain")}</button>
        <div className="flex gap-2">
          <ShareButton score={snap.score} level={snap.wave} finished={false} compact className="flex-1 justify-center" share={() => shareDaily(snap.score, snap.wave, seconds, saved?.rank_day)} />
          <button data-testid="daily-quit-button" onClick={onQuit} className="btn-ghost flex-1 whitespace-nowrap">{t("mainMenu")}</button>
        </div>
      </div>
    }>
      <span className="eyebrow text-sky-300">{t("daily")}</span>
      <h2 className="title-h2 mt-1">{t("dailyOver")}</h2>
      <div className="mt-4 flex gap-6 flex-wrap">
        <div><div className="hud-label">{t("score")}</div><div data-testid="daily-over-score" className="stat-value">{snap.score}</div></div>
        <div><div className="hud-label">{t("wave")}</div><div data-testid="daily-over-wave" className="stat-value">{snap.wave}</div></div>
        <div><div className="hud-label">{t("survived")}</div><div data-testid="daily-over-time" className="stat-value">{fmtSec(seconds)}</div></div>
      </div>
      {status !== "done" && (
        <div className="mt-5 flex gap-2">
          <input data-testid="daily-name-input" value={name} maxLength={16} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") submit(); }} placeholder={t("yourName")} className="name-input flex-1" />
          <button data-testid="daily-submit-button" onClick={submit} disabled={status === "sending" || !name.trim()} className="btn-ghost">{status === "sending" ? "…" : t("submit")}</button>
        </div>
      )}
      {status === "error" && <div data-testid="daily-submit-error" className="mt-2 text-sm text-red-400">{t("submitFailed")}</div>}
      {saved && (
        <div data-testid="daily-rank" className="mt-3 text-sm text-emerald-400 flex gap-4 flex-wrap">
          <span>{t("yourRankDay", { n: saved.rank_day ?? "-" })}</span>
          <span>{t("yourRankAll", { n: saved.rank_all ?? "-" })}</span>
        </div>
      )}
      <div className="mt-5"><DailyLeaderboard platform={platform} highlightId={saved?.id} refreshKey={refresh} /></div>
      {pack && <div className="mt-5"><PackTable pack={pack} /></div>}
    </Modal>
  );
};

export const GameOver = ({ snap, onRestart, onRetry, onQuit, packKey = 0 }: { snap: Snapshot; onRestart: () => void; onRetry: () => void; onQuit: () => void; packKey?: number }) => {
  const pack = usePack(packKey);
  const retryAt = snap.retryAt || snap.level;
  const [name, setName] = useState(loadName);
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [savedId, setSavedId] = useState<string | undefined>();
  const [refresh, setRefresh] = useState(0);

  const submit = async () => {
    const n = name.trim().slice(0, 16);
    if (!n) return;
    setStatus("sending");
    try {
      saveName(n);
      const r = await submitScore(n, snap.score, snap.level);
      setSavedId(r.id);
      setStatus("done");
      setRefresh((k) => k + 1);
    } catch {
      setStatus("error");
    }
  };

  return (
    <Modal testId="game-over-overlay" footer={
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <button data-testid="retry-level-button" onClick={onRetry} className="btn-primary flex-1">{t("retryLevel", { n: retryAt })}</button>
          <button data-testid="restart-game-button" onClick={onRestart} className="btn-ghost flex-1 whitespace-nowrap">{t("playAgain")}</button>
        </div>
        {retryAt < snap.level && <p data-testid="setback-note" className="text-xs text-center text-amber-200/80">{t("setbackNote", { n: retryAt })}</p>}
        <div className="flex gap-2">
          <ShareButton score={snap.score} level={snap.level} finished={false} compact className="flex-1 justify-center" />
          <button data-testid="game-over-menu-button" onClick={onQuit} className="btn-ghost flex-1 whitespace-nowrap">{t("mainMenu")}</button>
        </div>
      </div>
    }>
      <span className="eyebrow text-red-400">{t("gameOver")}</span>
      <h2 className="title-h2 mt-1">{t("pupTired")}</h2>
      <div className="mt-4 flex gap-6">
        <div><div className="hud-label">{t("score")}</div><div data-testid="game-over-score" className="stat-value">{snap.score}</div></div>
        <div><div className="hud-label">{t("best")}</div><div data-testid="game-over-best" className="stat-value">{snap.best}</div></div>
        <div><div className="hud-label">{t("level")}</div><div className="stat-value">{snap.level}</div></div>
      </div>
      {snap.score >= snap.best && snap.score > 0 && <div data-testid="new-record-badge" className="mt-3 text-amber-300 font-bold">{t("newRecord")}</div>}
      {status !== "done" && (
        <div className="mt-5 flex gap-2">
          <input
            data-testid="player-name-input"
            value={name}
            maxLength={16}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") submit(); }}
            placeholder={t("yourName")}
            className="name-input flex-1"
          />
          <button data-testid="submit-score-button" onClick={submit} disabled={status === "sending" || !name.trim()} className="btn-ghost">
            {status === "sending" ? "…" : t("submit")}
          </button>
        </div>
      )}
      {status === "error" && <div data-testid="submit-score-error" className="mt-2 text-sm text-red-400">{t("submitFailed")}</div>}
      {status === "done" && <div data-testid="submit-score-success" className="mt-3 text-sm text-emerald-400">{t("scoreSaved")}</div>}
      <div className="mt-5"><Leaderboard highlightId={savedId} refreshKey={refresh} /></div>
      {pack && <div className="mt-5"><PackTable pack={pack} /></div>}
    </Modal>
  );
};

export const ShareButton = ({ score, level, finished, compact = false, share, className = "" }: { score: number; level: number; finished: boolean; compact?: boolean; share?: () => Promise<ShareResult>; className?: string }) => {
  const [state, setState] = useState<"idle" | "busy" | "shared" | "downloaded" | "copied">("idle");
  const onShare = async () => {
    setState("busy");
    try { setState(await (share ? share() : shareScore(score, level, finished))); } catch { toast.error(t("shareTitle")); setState("idle"); }
  };
  const label = state === "busy" ? "…" : state === "shared" ? t("shared") : state === "copied" || state === "downloaded" ? t("linkCopied") : t("share");
  return (
    <button data-testid="share-score-button" onClick={onShare} disabled={state === "busy"} className={`btn-ghost flex items-center gap-2 whitespace-nowrap ${compact ? "px-3" : ""} ${className}`} title={t("shareTitle")}>
      <Share2 size={16} /> {label}
    </button>
  );
};

export const VolumeSliders = ({ sfx, music, onSfx, onMusic }: { sfx: number; music: number; onSfx: (v: number) => void; onMusic: (v: number) => void }) => (
  <div data-testid="volume-sliders" className="space-y-3">
    {[{ id: "sfx", label: t("sfxVolume"), v: sfx, on: onSfx }, { id: "music", label: t("musicVolume"), v: music, on: onMusic }].map((r) => (
      <label key={r.id} className="flex items-center gap-3 text-sm text-stone-300">
        <span className="hud-label w-24">{r.label}</span>
        <input data-testid={`${r.id}-volume-slider`} type="range" min={0} max={1} step={0.05} value={r.v} onChange={(e) => r.on(Number(e.target.value))} onKeyDown={(e) => e.stopPropagation()} className="vol-slider flex-1" aria-label={r.label} />
        <span data-testid={`${r.id}-volume-value`} className="font-mono text-xs w-9 text-right">{Math.round(r.v * 100)}%</span>
      </label>
    ))}
  </div>
);

export const ControlPicker = ({ mode, onChange, stick, onStick }: { mode: ControlMode; onChange: (m: ControlMode) => void; stick?: StickSize; onStick?: (s: StickSize) => void }) => (
  <div data-testid="control-picker" className="flex flex-col gap-2">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="hud-label w-24">{t("controls")}</span>
      <div className="flex gap-1" role="group">
        {CONTROL_MODES.map((m) => (
          <button key={m} type="button" data-testid={`control-${m}`} className={`lang-btn ${mode === m ? "lang-btn-on" : ""}`} aria-pressed={mode === m} onClick={() => onChange(m)}>{t(m === "dpad" ? "ctrlDpad" : m === "joystick" ? "ctrlJoystick" : "ctrlSwipe")}</button>
        ))}
      </div>
    </div>
    {mode === "joystick" && stick && onStick && (
      <div data-testid="stick-size-picker" className="flex items-center gap-2 flex-wrap">
        <span className="hud-label w-24">{t("stickSize")}</span>
        <div className="flex gap-1" role="group">
          {STICK_SIZES.map((s) => (
            <button key={s} type="button" data-testid={`stick-size-${s}`} className={`lang-btn ${stick === s ? "lang-btn-on" : ""}`} aria-pressed={stick === s} onClick={() => onStick(s)}>{s.toUpperCase()}</button>
          ))}
        </div>
      </div>
    )}
  </div>
);

export const Paused = ({ onResume, volumes, controls }: { onResume: () => void; volumes: { sfx: number; music: number; onSfx: (v: number) => void; onMusic: (v: number) => void }; controls?: { mode: ControlMode; onChange: (m: ControlMode) => void; stick: StickSize; onStick: (s: StickSize) => void } }) => (
  <Modal testId="pause-overlay">
    <h2 className="title-h2">{t("pause")}</h2>
    <p className="text-sm text-stone-400 mt-2">{t("pauseText")}</p>
    <div className="mt-4"><VolumeSliders {...volumes} /></div>
    {controls && <div className="mt-3"><ControlPicker {...controls} /></div>}
    <div className="mt-4"><LangSwitch /></div>
    <button data-testid="resume-game-button" onClick={onResume} className="btn-primary mt-6 w-full">{t("resume")}</button>
  </Modal>
);
